import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import FlagImg from '../components/FlagImg';
import '../styles/AdminPanel.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'CD', name: 'Democratic Republic of Congo' },
  { code: 'GH', name: 'Ghana' },
  { code: 'BR', name: 'Brazil' },
  { code: 'IN', name: 'India' },
  { code: 'CN', name: 'China' },
  { code: 'JP', name: 'Japan' },
  { code: 'AU', name: 'Australia' },
  { code: 'IE', name: 'Ireland' },
];

const SOURCE_TYPES = [
  'news',
  'twitter',
  'facebook',
  'bbc',
  'guardian',
  'reuters',
  'ap',
  'npr',
  'dw',
  'ngo',
  'radio',
  'newspaper',
  'social-media',
  'other',
];

function getCountryFlag(countryCode) {
  if (!countryCode || countryCode.length !== 2) {
    return '[]';
  }

  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function formatDate(value) {
  if (!value) {
    return 'Not yet';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function DocumentsSection({ adminToken }) {
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [openingDoc, setOpeningDoc] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_BASE}/admin/docs`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then((r) => setDocs(r.data))
      .catch(() => setDocs([]))
      .finally(() => setDocsLoading(false));
  }, [adminToken]);

  const openPdf = async (filename) => {
    setOpeningDoc(filename);
    try {
      const res = await fetch(`${API_BASE}/admin/docs/${filename}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert('Could not open document.');
    } finally {
      setOpeningDoc(null);
    }
  };

  return (
    <section className="docs-section">
      <div className="section-intro">
        <div>
          <h2>Corporate Documents</h2>
          <p>Internal PDFs — mission, legal templates, investor materials. Admin access only.</p>
        </div>
      </div>
      {docsLoading ? (
        <p className="loading-text">Loading documents…</p>
      ) : docs.length === 0 ? (
        <p className="empty-state">No documents found in docs/pdf/.</p>
      ) : (
        <div className="docs-grid">
          {docs.map((doc) => (
            <button
              key={doc.filename}
              className="doc-card"
              onClick={() => openPdf(doc.filename)}
              disabled={openingDoc === doc.filename}
              title={`Open ${doc.name}`}
            >
              <span className="doc-icon">📄</span>
              <div className="doc-info">
                <span className="doc-name">{doc.name}</span>
                <span className="doc-meta">
                  {openingDoc === doc.filename ? 'Opening…' : `${doc.size_kb} KB · PDF`}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function AdminPanel({ onCreateTopic, adminToken }) {
  const adminAxios = useMemo(() => {
    const instance = axios.create({ baseURL: API_BASE });
    if (adminToken) instance.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    return instance;
  }, [adminToken]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [countryCode, setCountryCode] = useState('US');
  const [region, setRegion] = useState('Americas');
  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceType, setSourceType] = useState('news');
  const [isFeatured, setIsFeatured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [scrapeUrl, setScrapeUrl] = useState('');
  const [scrapeSourceType, setScrapeSourceType] = useState('news');
  const [scraping, setScraping] = useState(false);
  const [scrapePreview, setScrapePreview] = useState(null);

  const [automationSources, setAutomationSources] = useState([]);
  const [draftQueue, setDraftQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [sourceSubmitting, setSourceSubmitting] = useState(false);
  const [automationRunning, setAutomationRunning] = useState(false);
  const [activeSourceRunId, setActiveSourceRunId] = useState(null);
  const [activeDraftActionId, setActiveDraftActionId] = useState(null);
  const [draftActionError, setDraftActionError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);

  // Filter state — Review Queue
  const [draftSearch, setDraftSearch] = useState('');
  const [draftCountryFilter, setDraftCountryFilter] = useState('');
  const [draftStatusFilter, setDraftStatusFilter] = useState('');

  // Filter state — Active Sources
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('');
  const [sourceStatusFilter, setSourceStatusFilter] = useState('');

  const [sourceName, setSourceName] = useState('');
  const [sourceFeedUrl, setSourceFeedUrl] = useState('');
  const [sourceRegistryType, setSourceRegistryType] = useState('news');
  const [sourceRegistryCountry, setSourceRegistryCountry] = useState('US');
  const [sourceRegistryRegion, setSourceRegistryRegion] = useState('Global');
  const [sourcePollMinutes, setSourcePollMinutes] = useState(60);

  useEffect(() => {
    fetchAutomationData();
  }, []);

  const fetchAutomationData = async () => {
    setQueueLoading(true);
    try {
      const [sourcesResponse, draftsResponse] = await Promise.all([
        adminAxios.get(`/admin/scrape-sources`),
        adminAxios.get(`/admin/topic-drafts`),
      ]);
      setAutomationSources(sourcesResponse.data);
      setDraftQueue(draftsResponse.data);
    } catch (error) {
      if (error.response?.status === 401) {
        setSessionExpired(true);
      } else {
        console.error('Error loading automation data:', error);
      }
    } finally {
      setQueueLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setCountryCode('US');
    setRegion('Americas');
    setSourceUrl('');
    setSourceType('news');
    setIsFeatured(false);
  };

  const loadDraftIntoEditor = (draft) => {
    setTitle(draft.title || '');
    setDescription(draft.description || '');
    setCountryCode(draft.country_code || 'US');
    setRegion(draft.region || 'Global');
    setSourceUrl(draft.source_url || '');
    setSourceType(draft.source_type || 'news');
    setIsFeatured(false);
    // Clear any previous scrape preview so the editor shows only this draft's data
    setScrapePreview(null);
    setScrapeUrl('');
    // Scroll to the Publish Event form
    setTimeout(() => {
      const el = document.querySelector('.admin-form-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleScrape = async (event) => {
    event.preventDefault();

    if (!scrapeUrl.trim()) {
      alert('Please paste a source URL to scrape.');
      return;
    }

    setScraping(true);
    try {
      const response = await adminAxios.post(`/admin/topics/scrape-preview`, {
        source_url: scrapeUrl.trim(),
        source_type: scrapeSourceType,
        fallback_country_code: countryCode,
        fallback_region: region,
      });

      const preview = response.data;
      setScrapePreview(preview);
      setTitle(preview.title || '');
      setDescription(preview.description || '');
      setCountryCode(preview.country_code || countryCode);
      setRegion(preview.region || region);
      setSourceUrl(preview.source_url || scrapeUrl.trim());
      setSourceType(preview.source_type || scrapeSourceType);
    } catch (error) {
      alert(`Error scraping source: ${error.response?.data?.detail || error.message}`);
    } finally {
      setScraping(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!title.trim() || !description.trim()) {
      alert('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      await adminAxios.post(`/topics`, {
        title,
        description,
        country_code: countryCode,
        region,
        source_url: sourceUrl || null,
        source_type: sourceType,
        is_featured: isFeatured,
      });

      setSuccess(true);
      setScrapePreview(null);
      setScrapeUrl('');
      resetForm();

      setTimeout(() => setSuccess(false), 3000);
      onCreateTopic();
      fetchAutomationData();
    } catch (error) {
      alert(`Error creating topic: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSourceCreate = async (event) => {
    event.preventDefault();

    if (!sourceName.trim() || !sourceFeedUrl.trim()) {
      alert('Please name the source and provide a feed or watch URL.');
      return;
    }

    setSourceSubmitting(true);
    try {
      await adminAxios.post(`/admin/scrape-sources`, {
        name: sourceName.trim(),
        source_url: sourceFeedUrl.trim(),
        source_type: sourceRegistryType,
        country_code: sourceRegistryCountry,
        region: sourceRegistryRegion.trim() || null,
        poll_minutes: Number(sourcePollMinutes),
        is_active: true,
      });

      setSourceName('');
      setSourceFeedUrl('');
      setSourceRegistryType('news');
      setSourceRegistryCountry('US');
      setSourceRegistryRegion('Global');
      setSourcePollMinutes(60);
      fetchAutomationData();
    } catch (error) {
      alert(`Error creating source: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSourceSubmitting(false);
    }
  };

  const runAutomationNow = async () => {
    setAutomationRunning(true);
    try {
      const response = await adminAxios.post(`/admin/automation/run-due?force=true`);
      await fetchAutomationData();
      alert(`Automation checked ${response.data.checked_sources} sources and created ${response.data.created_drafts} new drafts.`);
    } catch (error) {
      alert(`Error running automation: ${error.response?.data?.detail || error.message}`);
    } finally {
      setAutomationRunning(false);
    }
  };

  const runSingleSource = async (sourceId) => {
    setActiveSourceRunId(sourceId);
    try {
      const response = await adminAxios.post(`/admin/scrape-sources/${sourceId}/run`);
      await fetchAutomationData();
      alert(`Source run complete. ${response.data.created} new drafts created.`);
    } catch (error) {
      alert(`Error running source: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActiveSourceRunId(null);
    }
  };

  const publishDraft = async (draftId) => {
    setActiveDraftActionId(draftId);
    setDraftActionError('');
    try {
      // Build a fresh axios instance each time so we always use the latest token
      const ax = axios.create({ baseURL: API_BASE });
      if (adminToken) ax.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      await ax.post(`/admin/topic-drafts/${draftId}/publish`, {
        review_notes: 'Published from admin review queue.',
      });
      await fetchAutomationData();
      onCreateTopic();
    } catch (error) {
      setDraftActionError(`Publish failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActiveDraftActionId(null);
    }
  };

  const rejectDraft = async (draftId) => {
    setActiveDraftActionId(draftId);
    setDraftActionError('');
    try {
      const ax = axios.create({ baseURL: API_BASE });
      if (adminToken) ax.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      await ax.post(`/admin/topic-drafts/${draftId}/reject`, {
        review_notes: 'Rejected from admin review queue.',
      });
      await fetchAutomationData();
    } catch (error) {
      setDraftActionError(`Reject failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setActiveDraftActionId(null);
    }
  };

  // Filtered Review Queue
  const filteredDrafts = useMemo(() => {
    const q = draftSearch.toLowerCase();
    return draftQueue.filter(d => {
      const matchSearch = !q ||
        (d.title || '').toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q) ||
        (d.publisher || '').toLowerCase().includes(q);
      const matchCountry = !draftCountryFilter || d.country_code === draftCountryFilter;
      const matchStatus = !draftStatusFilter || d.status === draftStatusFilter;
      return matchSearch && matchCountry && matchStatus;
    });
  }, [draftQueue, draftSearch, draftCountryFilter, draftStatusFilter]);

  // Filtered Sources
  const filteredSources = useMemo(() => {
    const q = sourceSearch.toLowerCase();
    return automationSources.filter(s => {
      const matchSearch = !q ||
        (s.name || '').toLowerCase().includes(q) ||
        (s.source_url || '').toLowerCase().includes(q);
      const matchType = !sourceTypeFilter || s.source_type === sourceTypeFilter;
      const matchStatus = sourceStatusFilter === '' ? true :
        sourceStatusFilter === 'active' ? s.is_active : !s.is_active;
      return matchSearch && matchType && matchStatus;
    });
  }, [automationSources, sourceSearch, sourceTypeFilter, sourceStatusFilter]);

  // Unique countries and source types in draft queue
  const draftCountries = useMemo(() =>
    [...new Set(draftQueue.map(d => d.country_code).filter(Boolean))].sort(),
    [draftQueue]
  );
  const draftStatuses = useMemo(() =>
    [...new Set(draftQueue.map(d => d.status).filter(Boolean))].sort(),
    [draftQueue]
  );
  const sourceTypes = useMemo(() =>
    [...new Set(automationSources.map(s => s.source_type).filter(Boolean))].sort(),
    [automationSources]
  );

  if (sessionExpired) {
    return (
      <div className="admin-panel">
        <div style={{ maxWidth: 480, margin: '4rem auto', padding: '2rem', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔒</div>
          <h2 style={{ marginBottom: '0.5rem' }}>Session Expired</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>Your admin session has timed out. Please sign out and log back in to continue.</p>
          <button
            onClick={() => { localStorage.removeItem('protesteAdminToken'); window.location.reload(); }}
            style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 9999, padding: '0.65rem 1.5rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.9375rem' }}
          >
            Sign out &amp; return
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>Admin Panel</h1>
        <p>Keep manual scraping, add scheduled collection, and review Claude-generated drafts before they become live civic events.</p>
      </header>

      <section className="automation-section">
        <div className="section-intro automation-intro">
          <div>
            <h2>Automated Ingestion</h2>
            <p>Register trusted feeds or watch pages, let the scheduler collect candidate articles, then review the queue before publishing.</p>
          </div>
          <div className="automation-actions">
            <button type="button" className="secondary-btn" onClick={fetchAutomationData} disabled={queueLoading}>
              Refresh Queue
            </button>
            <button type="button" className="submit-btn" onClick={runAutomationNow} disabled={automationRunning}>
              {automationRunning ? 'Running Automation...' : 'Run Due Sources Now'}
            </button>
          </div>
        </div>

        <div className="automation-grid">
          <div className="automation-card">
            <h3>Register Source</h3>
            <form onSubmit={handleSourceCreate} className="admin-form">
              <div className="form-group">
                <label>Source Name</label>
                <input
                  type="text"
                  value={sourceName}
                  onChange={(event) => setSourceName(event.target.value)}
                  placeholder="e.g., Reuters World Feed"
                  required
                />
              </div>

              <div className="form-group">
                <label>Feed or Watch URL</label>
                <input
                  type="url"
                  value={sourceFeedUrl}
                  onChange={(event) => setSourceFeedUrl(event.target.value)}
                  placeholder="https://example.com/rss or article watch page"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Source Type</label>
                  <select
                    value={sourceRegistryType}
                    onChange={(event) => setSourceRegistryType(event.target.value)}
                  >
                    {SOURCE_TYPES.map((source) => (
                      <option key={source} value={source}>
                        {source.replace('-', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Poll Frequency (minutes)</label>
                  <input
                    type="number"
                    min="5"
                    step="5"
                    value={sourcePollMinutes}
                    onChange={(event) => setSourcePollMinutes(event.target.value)}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fallback Country</label>
                  <select
                    value={sourceRegistryCountry}
                    onChange={(event) => setSourceRegistryCountry(event.target.value)}
                  >
                    {COUNTRIES.map((country) => (
                      <option key={country.code} value={country.code}>
                        {getCountryFlag(country.code)} {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Fallback Region</label>
                  <input
                    type="text"
                    value={sourceRegistryRegion}
                    onChange={(event) => setSourceRegistryRegion(event.target.value)}
                    placeholder="e.g., Europe or Lagos, Nigeria"
                  />
                </div>
              </div>

              <button type="submit" disabled={sourceSubmitting} className="submit-btn">
                {sourceSubmitting ? 'Saving Source...' : 'Register Source'}
              </button>
            </form>
          </div>

          <div className="automation-card">
            <div className="card-title-row">
              <h3>Sources</h3>
              <span className="count-pill">{filteredSources.length} / {automationSources.length}</span>
            </div>

            {/* Source filters */}
            <div className="admin-filter-bar">
              <input
                className="admin-filter-input"
                type="text"
                placeholder="Search by name or URL…"
                value={sourceSearch}
                onChange={e => setSourceSearch(e.target.value)}
              />
              <select className="admin-filter-select" value={sourceTypeFilter} onChange={e => setSourceTypeFilter(e.target.value)}>
                <option value="">All types</option>
                {sourceTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="admin-filter-select" value={sourceStatusFilter} onChange={e => setSourceStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
              {(sourceSearch || sourceTypeFilter || sourceStatusFilter) && (
                <button className="admin-filter-clear" onClick={() => { setSourceSearch(''); setSourceTypeFilter(''); setSourceStatusFilter(''); }}>Clear</button>
              )}
            </div>

            {queueLoading ? (
              <p className="empty-hint">Loading source registry...</p>
            ) : filteredSources.length === 0 ? (
              <p className="empty-hint">{automationSources.length === 0 ? 'No automated sources registered yet.' : 'No sources match your filters.'}</p>
            ) : (
              <div className="source-list">
                {filteredSources.map((source) => (
                  <div key={source.id} className={`source-card ${source.last_error ? 'source-card--error' : ''}`}>
                    <div className="source-card-top">
                      <div>
                        <strong>{source.name}</strong>
                        <p>{source.source_url}</p>
                      </div>
                      <span className={`status-pill ${source.is_active ? 'is-active' : 'is-paused'}`}>
                        {source.is_active ? 'Active' : 'Paused'}
                      </span>
                    </div>

                    <div className="source-meta">
                      <span>{source.source_type}</span>
                      <span>{source.region || 'Global'}</span>
                      <span>Every {source.poll_minutes} min</span>
                    </div>

                    <div className="source-health">
                      <span>Last checked: {formatDate(source.last_checked_at)}</span>
                      <span>Last success: {formatDate(source.last_success_at)}</span>
                      {source.last_error && <span className="error-inline">Last error: {source.last_error}</span>}
                    </div>

                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() => runSingleSource(source.id)}
                      disabled={activeSourceRunId === source.id}
                    >
                      {activeSourceRunId === source.id ? 'Running...' : 'Run This Source'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="queue-section">
        <div className="card-title-row">
          <h2>Review Queue</h2>
          <span className="count-pill">{filteredDrafts.length} / {draftQueue.length}</span>
        </div>

        {/* Draft queue filters */}
        <div className="admin-filter-bar">
          <input
            className="admin-filter-input"
            type="text"
            placeholder="Search title, description, publisher…"
            value={draftSearch}
            onChange={e => setDraftSearch(e.target.value)}
          />
          <select className="admin-filter-select" value={draftCountryFilter} onChange={e => setDraftCountryFilter(e.target.value)}>
            <option value="">All countries</option>
            {draftCountries.map(c => (
              <option key={c} value={c}>{getCountryFlag(c)} {c}</option>
            ))}
          </select>
          <select className="admin-filter-select" value={draftStatusFilter} onChange={e => setDraftStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {draftStatuses.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          {(draftSearch || draftCountryFilter || draftStatusFilter) && (
            <button className="admin-filter-clear" onClick={() => { setDraftSearch(''); setDraftCountryFilter(''); setDraftStatusFilter(''); }}>Clear</button>
          )}
        </div>

        {draftActionError && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '0.75rem', fontSize: '0.875rem', fontWeight: 500 }}>
            {draftActionError}
            <button onClick={() => setDraftActionError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontSize: '1.1rem', lineHeight: 1 }}>×</button>
          </div>
        )}

        {queueLoading ? (
          <p className="empty-hint">Loading queued drafts...</p>
        ) : filteredDrafts.length === 0 ? (
          <p className="empty-hint">{draftQueue.length === 0 ? 'No drafts waiting for review. Run the automation or scrape a source manually.' : 'No drafts match your filters.'}</p>
        ) : (
          <div className="draft-list">
            {filteredDrafts.map((draft) => (
              <article key={draft.id} className="draft-card">
                <div className="draft-card-top">
                  <div>
                    <span className="preview-label">{draft.publisher || 'Automated draft'}</span>
                    <h3>{draft.title}</h3>
                  </div>
                  <span className="preview-chip">{draft.status.replace('_', ' ')}</span>
                </div>

                {draft.image_url && (
                  <img
                    src={draft.image_url}
                    alt=""
                    className="draft-image"
                    loading="lazy"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                )}

                <p className="draft-description">{draft.description}</p>

                <div className="preview-meta">
                  <span>{draft.country_code ? <><FlagImg code={draft.country_code} size={14} style={{ marginRight: 3 }} />{draft.country_code}</> : 'Country unclear'}</span>
                  <span>{draft.region || 'Region unclear'}</span>
                  <span>{draft.incident_date || 'Date unclear'}</span>
                </div>

                <div className="preview-block">
                  <strong>Source summary</strong>
                  <p>{draft.source_summary || draft.extracted_excerpt}</p>
                </div>

                {draft.relevance_reason && (
                  <div className="preview-block">
                    <strong>Relevance</strong>
                    <p>{draft.relevance_reason}</p>
                  </div>
                )}

                {draft.warnings && draft.warnings.length > 0 && (
                  <div className="preview-warnings">
                    <strong>Warnings</strong>
                    <ul>
                      {draft.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="draft-actions">
                  <button type="button" className="ghost-btn" onClick={() => loadDraftIntoEditor(draft)}>
                    Load Into Editor
                  </button>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => rejectDraft(draft.id)}
                    disabled={activeDraftActionId === draft.id}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={() => publishDraft(draft.id)}
                    disabled={activeDraftActionId === draft.id}
                  >
                    {activeDraftActionId === draft.id ? 'Saving...' : 'Publish Draft'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="scrape-section">
        <div className="section-intro">
          <h2>Manual Scrape Into Draft</h2>
          <p>Keep using the direct source-to-draft flow whenever you want to inspect an article yourself before it ever enters the automated queue.</p>
        </div>

        <form onSubmit={handleScrape} className="scrape-form">
          <div className="form-group">
            <label>Source URL</label>
            <input
              type="url"
              value={scrapeUrl}
              onChange={(event) => setScrapeUrl(event.target.value)}
              placeholder="https://example.com/article"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Expected Source Type</label>
              <select
                value={scrapeSourceType}
                onChange={(event) => setScrapeSourceType(event.target.value)}
              >
                {SOURCE_TYPES.map((source) => (
                  <option key={source} value={source}>
                    {source.replace('-', ' ').toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Fallback Country For AI</label>
              <select
                value={countryCode}
                onChange={(event) => setCountryCode(event.target.value)}
              >
                {COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {getCountryFlag(country.code)} {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button type="submit" disabled={scraping} className="submit-btn">
            {scraping ? 'Scraping Source...' : 'Scrape With Claude'}
          </button>
        </form>

        {scrapePreview && (
          <div className="scrape-preview">
            <div className="preview-header">
              <div>
                <span className="preview-label">Draft Ready</span>
                <h3>{scrapePreview.title}</h3>
              </div>
              <span className="preview-chip">{scrapePreview.publisher || 'Unknown source'}</span>
            </div>

            {scrapePreview.image_url && (
              <img
                src={scrapePreview.image_url}
                alt=""
                className="draft-image"
                loading="lazy"
                onError={e => { e.target.style.display = 'none'; }}
              />
            )}

            <p className="preview-summary">{scrapePreview.source_summary}</p>

            <div className="preview-meta">
              <span>{scrapePreview.country_code || 'No country detected'}</span>
              <span>{scrapePreview.region || 'No region detected'}</span>
              <span>{scrapePreview.incident_date || 'Date unclear'}</span>
            </div>

            <div className="preview-block">
              <strong>Why this belongs on Proteste</strong>
              <p>{scrapePreview.relevance_reason || 'Review the source and confirm it fits the platform.'}</p>
            </div>

            <div className="preview-block">
              <strong>Extracted source excerpt</strong>
              <p>{scrapePreview.extracted_excerpt}</p>
            </div>

            {scrapePreview.warnings && scrapePreview.warnings.length > 0 && (
              <div className="preview-warnings">
                <strong>Review before publishing</strong>
                <ul>
                  {scrapePreview.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>

      <div className="admin-form-section">
        <h2>Publish Event</h2>

        {success && (
          <div className="success-message">
            Event created successfully.
          </div>
        )}

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label>Incident Title *</label>
            <input
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="e.g., Police Restraint Death in Dublin, January 2024"
              required
            />
            <small>Keep the title short, factual, and neutral.</small>
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Provide a neutral, factual description of what happened..."
              rows={6}
              required
            />
            <small>This can be Claude’s draft summary or your own reviewed event description.</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Country of Incident *</label>
              <select
                value={countryCode}
                onChange={(event) => setCountryCode(event.target.value)}
              >
                {COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {getCountryFlag(country.code)} {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Region / Location *</label>
              <input
                type="text"
                value={region}
                onChange={(event) => setRegion(event.target.value)}
                placeholder="e.g., Dublin, Ireland or Europe"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Source Type</label>
              <select
                value={sourceType}
                onChange={(event) => setSourceType(event.target.value)}
              >
                {SOURCE_TYPES.map((source) => (
                  <option key={source} value={source}>
                    {source.replace('-', ' ').toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Source URL</label>
              <input
                type="url"
                value={sourceUrl}
                onChange={(event) => setSourceUrl(event.target.value)}
                placeholder="https://example.com/article"
              />
              <small>Original source used for verification and scraping.</small>
            </div>
          </div>

          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={isFeatured}
              onChange={(event) => setIsFeatured(event.target.checked)}
            />
            <span>Mark this event as featured on the dashboard</span>
          </label>

          <button type="submit" disabled={loading} className="submit-btn">
            {loading ? 'Creating Event...' : 'Publish Event'}
          </button>
        </form>
      </div>

      <DocumentsSection adminToken={adminToken} />

      <section className="admin-guidelines">
        <h2>Review Guidelines</h2>
        <div className="guidelines-grid">
          <div className="guideline">
            <h4>Use verified reporting</h4>
            <p>Only ingest sources that can be traced to credible journalism, civil-society reporting, or documented eyewitness coverage.</p>
          </div>
          <div className="guideline">
            <h4>Keep Claude grounded</h4>
            <p>Review the generated draft and remove anything that goes beyond what the source actually supports.</p>
          </div>
          <div className="guideline">
            <h4>Publish neutral summaries</h4>
            <p>Descriptions should focus on what happened, who is affected, and why public accountability matters.</p>
          </div>
          <div className="guideline">
            <h4>Watch for duplicates</h4>
            <p>Before publishing, check whether the incident already exists so reactions stay in one shared event thread.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AdminPanel;

